import * as React from 'react';
import Collapse from 'antd/lib/collapse';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

import { StepRun } from '@/types/stepRun';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { StepRunComponentProps } from '@/typescriptHelpers';
import { selectReleaseVersion, selectWorkflowId, selectWorkflowRunId } from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { AutomationsSection } from './AutomationsSection';
import { ChecklistSection } from './ChecklistSection';
import WorkflowsPlatformIcon from '../icons/WorkflowsPlatformIcon';
import { Space } from 'antd/lib';
import { createStepRunRoute, ROUTER_REDIRECT } from '@/helpers/routeHelpers';
import { useHistory } from 'react-router-dom';

export interface StepRunPanelProps {
    orgId: string;
    appId: string;
    releaseId: string;
    stepRun: StepRun;
}

export const StepRunPanel: React.FC<StepRunPanelProps> = ({ orgId, appId, releaseId, stepRun }) => {
    const stepName = stepRun.displayName || stepRun.stepId;

    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));

    const stepRunComponentProps: StepRunComponentProps = {
        orgId,
        appId,
        releaseId,
        workflowId,
        workflowRunId,
        stepConfigId: stepRun.stepId,
        stepRunId: stepRun.id,
    };

    const history = useHistory();

    const releaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, releaseId));
    return (
        <Collapse
            collapsible="icon"
            activeKey={[stepRun.id]}
            style={{
                backgroundColor: antGrey.grey2,
                borderRadius: '8px',
                border: `1px solid ${antGrey.grey5}`,
            }}
            items={[
                {
                    key: stepRun.id,
                    label: (
                        <Flex align="center" justify="space-between" style={{ width: '100%' }}>
                            <Space>
                                {stepRun.destinations.map((destination) => (
                                    <WorkflowsPlatformIcon platform={destination.platform} width={18} height={18} />
                                ))}
                                <Text
                                    strong
                                    style={{
                                        fontSize: 14,
                                        color: antGrey.grey8,
                                    }}
                                >
                                    {stepName}
                                </Text>
                            </Space>

                            <Button
                                type="link"
                                size="small"
                                onClick={() => {
                                    history.push(createStepRunRoute(orgId, appId, releaseVersion, stepRun.id), {
                                        [ROUTER_REDIRECT]: true,
                                    });
                                }}
                                style={{
                                    color: runwayColors.info,
                                    padding: 0,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    gap: 4,
                                }}
                            >
                                View
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: runwayColors.info,
                                        borderRadius: '50%',
                                        width: 18,
                                        height: 18,
                                        marginLeft: 4,
                                    }}
                                >
                                    <ArrowRightOutlined style={{ fontSize: 10, color: antGrey.grey2 }} />
                                </div>
                            </Button>
                        </Flex>
                    ),
                    children: (
                        <div>
                            <AutomationsSection automationRuns={stepRun.automationRuns} />
                            <ChecklistSection
                                stepRunComponentProps={stepRunComponentProps}
                                releaseStepType={stepRun.type}
                                checklistItems={stepRun.checklistItems}
                            />
                        </div>
                    ),
                    showArrow: false,
                    style: {
                        borderBottom: 'none',
                    },
                },
            ]}
        />
    );
};
